import React from "react";
import moment from "moment";
import {
  COLUMNAS_DETALLE,
  detalleTicket,
  celdaTexto,
  formatearCantidad,
  formatearMonto,
} from "./detalleNotaEntrega";

/*
 * Reporte PDF (react-pdf) dedicado a la comparacion de una nota de entrega
 * eliminada contra su respaldo (tablas notaentregarespaldo +
 * notaentregadetallerespaldo).
 *
 * Se genera con el mismo bundle de react-pdf que el resto de reportes
 * (public/lib/reactpdf.bundle.js) y se muestra/imprime desde
 * reporteAuditoriaPDF.mostrarReporteAuditoria cuando la opcion `comparacion`
 * viene informada (ver src/components/configuracionGeneral/auditoria.js).
 *
 * opciones = {
 *   registros,   arreglo con los registros consultados (incluye el eliminado)
 *   filtros,     filtros aplicados en pantalla (documento, status, desde, hasta)
 *   comparacion, arreglo [{ tipo, titulo, subtitulo, icono, campos, nota, detalle }]
 *                el primero es el registro de auditoria y el resto los respaldos
 *   metadatos,   { empresa, rif, usuario, logo }
 * }
 */

const texto = (valor) => {
  if (valor === undefined || valor === null) {
    return "";
  }
  if (valor instanceof Date) {
    return moment(valor).format("DD-MM-YYYY hh:mm a");
  }
  if (typeof valor === "object") {
    try {
      return JSON.stringify(valor);
    } catch (error) {
      return "";
    }
  }
  return String(valor);
};

const metaTexto = (nombre, valor, vacio) =>
  nombre + ": " + (texto(valor) === "" ? vacio || "TODAS" : texto(valor));

const crearEstilos = (StyleSheet) =>
  StyleSheet.create({
    pagina: {
      paddingTop: 76,
      paddingBottom: 40,
      paddingHorizontal: 24,
      fontSize: 8,
      fontFamily: "Helvetica",
      color: "#232323",
    },
    encabezado: {
      position: "absolute",
      top: 16,
      left: 24,
      right: 24,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0b3d63",
      paddingBottom: 5,
    },
    encabezadoIzq: {
      flexDirection: "row",
      alignItems: "center",
    },
    logo: {
      width: 46,
      height: 46,
      marginRight: 8,
      objectFit: "contain",
    },
    titulo: {
      fontSize: 14,
      fontFamily: "Helvetica-Bold",
      color: "#0b3d63",
    },
    subtitulo: {
      fontSize: 8,
      color: "#4d4d4d",
    },
    barra: {
      borderWidth: 1,
      borderColor: "#d5dee6",
      backgroundColor: "#f2f6fa",
      paddingVertical: 5,
      paddingHorizontal: 6,
      marginBottom: 10,
    },
    barraTitulo: {
      fontSize: 8,
      fontFamily: "Helvetica-Bold",
      color: "#0b3d63",
      marginBottom: 3,
    },
    barraFila: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    meta: {
      fontSize: 7.5,
      color: "#4d4d4d",
      marginRight: 12,
    },
    seccion: {
      marginBottom: 12,
    },
    seccionTitulo: {
      fontSize: 9,
      fontFamily: "Helvetica-Bold",
      color: "#ffffff",
      backgroundColor: "#0b3d63",
      paddingVertical: 3,
      paddingHorizontal: 5,
      marginBottom: 5,
    },
    datos: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    dato: {
      width: "33.33%",
      paddingRight: 10,
      marginBottom: 4,
    },
    datoLabel: {
      fontSize: 6.5,
      color: "#6c757d",
    },
    datoValor: {
      fontSize: 8,
      fontFamily: "Helvetica-Bold",
      color: "#232323",
    },
    filaTitulos: {
      flexDirection: "row",
      backgroundColor: "#055182",
      marginTop: 4,
    },
    tituloColumna: {
      fontSize: 7.5,
      fontFamily: "Helvetica-Bold",
      color: "#ffffff",
      paddingVertical: 4,
      paddingHorizontal: 4,
    },
    fila: {
      flexDirection: "row",
      borderBottomWidth: 0.5,
      borderBottomColor: "#dfe6ec",
    },
    filaPar: {
      backgroundColor: "#f7f9fb",
    },
    celda: {
      fontSize: 7.5,
      paddingVertical: 3,
      paddingHorizontal: 4,
    },
    celdaDer: {
      textAlign: "right",
    },
    negrita: {
      fontFamily: "Helvetica-Bold",
    },
    filaTotal: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderTopColor: "#0b3d63",
      backgroundColor: "#eef3f8",
    },
    celdaTotal: {
      fontSize: 8,
      fontFamily: "Helvetica-Bold",
      color: "#0b3d63",
      paddingVertical: 4,
      paddingHorizontal: 4,
    },
    sinDetalle: {
      fontSize: 7.5,
      color: "#6c757d",
      marginTop: 3,
    },
    firmas: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 30,
    },
    firma: {
      width: "30%",
      borderTopWidth: 0.8,
      borderTopColor: "#8a99a8",
      paddingTop: 3,
    },
    firmaTexto: {
      fontSize: 7,
      color: "#5c6b7a",
      textAlign: "center",
    },
    pieFijo: {
      position: "absolute",
      bottom: 16,
      left: 24,
      right: 24,
      flexDirection: "row",
      justifyContent: "space-between",
      borderTopWidth: 0.5,
      borderTopColor: "#d5dee6",
      paddingTop: 3,
    },
    pieTexto: {
      fontSize: 7,
      color: "#6c757d",
    },
    sinDatos: {
      marginTop: 30,
      textAlign: "center",
      fontSize: 10,
      color: "#6c757d",
    },
  });

/*
 * Documento react-pdf del reporte de comparacion de una nota de entrega
 * eliminada contra su respaldo. R = window.ReactPDF
 */
export const crearDocumentoNotaEntregaRespaldo = (R, opciones) => {
  const { Document, Page, View, Text, Image } = R;
  const estilos = crearEstilos(R.StyleSheet);

  const datos = opciones || {};
  const metadatos = datos.metadatos || {};
  const filtros = datos.filtros || {};
  const lista = Array.isArray(datos.registros) ? datos.registros : [];
  const comparacion = Array.isArray(datos.comparacion) ? datos.comparacion : [];

  const registro =
    comparacion.filter((columna) => columna.tipo === "auditoria")[0] ||
    comparacion[0] ||
    null;
  const respaldos = comparacion.filter((columna) => columna.tipo === "notaentrega");
  const camposRegistro =
    registro && Array.isArray(registro.campos) ? registro.campos : [];

  return (
    <Document
      title="Reporte de Nota de Entrega (Respaldo)"
      author={texto(metadatos.empresa)}
      subject="Datos Eliminados (tabla auditadataeliminada)"
      creator="ATHOS ERP"
      producer="ATHOS ERP"
    >
      <Page size="A4" orientation="portrait" style={estilos.pagina}>
        <View style={estilos.encabezado} fixed>
          <View style={estilos.encabezadoIzq}>
            {metadatos.logo ? (
              <Image src={metadatos.logo} style={estilos.logo} />
            ) : null}
            <View>
              <Text style={estilos.titulo}>REPORTE DE NOTA DE ENTREGA</Text>
              <Text style={estilos.subtitulo}>{texto(metadatos.empresa)}</Text>
              {texto(metadatos.rif) === "" ? null : (
                <Text style={estilos.subtitulo}>
                  {"RIF: " + texto(metadatos.rif)}
                </Text>
              )}
            </View>
          </View>
          <View>
            <Text style={estilos.subtitulo}>
              {"Emitido: " + moment().format("DD-MM-YYYY hh:mm a")}
            </Text>
            <Text style={estilos.subtitulo}>
              {"Usuario: " + texto(metadatos.usuario)}
            </Text>
            <Text style={estilos.subtitulo}>
              {"Datos Eliminados (tabla auditadataeliminada)"}
            </Text>
          </View>
        </View>

        <View style={estilos.barra}>
          <Text style={estilos.barraTitulo}>
            {"COMPARACIÓN CON EL RESPALDO (" + respaldos.length + ")"}
          </Text>
          <View style={estilos.barraFila}>
            <Text style={estilos.meta}>
              {metaTexto("DOCUMENTO", filtros.tabla)}
            </Text>
            <Text style={estilos.meta}>
              {metaTexto("STATUS", filtros.accion)}
            </Text>
            <Text style={estilos.meta}>{metaTexto("DESDE", filtros.desde, "-")}</Text>
            <Text style={estilos.meta}>{metaTexto("HASTA", filtros.hasta, "-")}</Text>
            <Text style={estilos.meta}>{"REGISTROS: " + lista.length}</Text>
          </View>
        </View>

        {!registro && (
          <Text style={estilos.sinDatos}>No hay informacion por mostrar</Text>
        )}

        {registro && (
          <View style={estilos.seccion}>
            <Text style={estilos.seccionTitulo}>
              {texto(
                registro.titulo || "DOCUMENTO ELIMINADO (AUDITORÍA)"
              ).toUpperCase()}
            </Text>
            <View style={estilos.datos}>
              {camposRegistro.map((campo, indice) => (
                <View style={estilos.dato} key={indice} wrap={false}>
                  <Text style={estilos.datoLabel}>
                    {texto(campo.label).toUpperCase()}
                  </Text>
                  <Text style={estilos.datoValor}>{texto(campo.valor)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}


        {respaldos.map((respaldo, indice) => {
          const { filas, totales } = detalleTicket(respaldo.detalle);
          return (
            <View style={estilos.seccion} key={indice}>
              <Text style={estilos.seccionTitulo}>
                {texto(
                  respaldo.titulo || "NOTA DE ENTREGA (RESPALDO)"
                ).toUpperCase()}
              </Text>
              {texto(respaldo.subtitulo) === "" ? null : (
                <Text style={estilos.meta}>{texto(respaldo.subtitulo)}</Text>
              )}
              <View style={estilos.datos}>
                {(respaldo.campos || []).map((campo, posicion) => (
                  <View style={estilos.dato} key={posicion} wrap={false}>
                    <Text style={estilos.datoLabel}>
                      {texto(campo.label).toUpperCase()}
                    </Text>
                    <Text style={estilos.datoValor}>{texto(campo.valor)}</Text>
                  </View>
                ))}
              </View>

              {filas.length === 0 ? (
                <Text style={estilos.sinDetalle}>
                  La nota no tiene renglones para mostrar.
                </Text>
              ) : (
                <View>
                  <View style={estilos.filaTitulos} wrap={false}>
                    {COLUMNAS_DETALLE.map((columna, posicion) => (
                      <Text
                        key={posicion}
                        style={[estilos.tituloColumna, { flex: columna.flex }]}
                      >
                        {columna.label}
                      </Text>
                    ))}
                  </View>
                  {filas.map((fila, posicion) => (
                    <View
                      key={posicion}
                      style={[
                        estilos.fila,
                        posicion % 2 === 1 ? estilos.filaPar : null,
                      ]}
                      wrap={false}
                    >
                      {COLUMNAS_DETALLE.map((columna, celda) => (
                        <Text
                          key={celda}
                          style={[
                            estilos.celda,
                            { flex: columna.flex },
                            columna.align === "right" ? estilos.celdaDer : null,
                            columna.key === "producto" ? estilos.negrita : null,
                          ]}
                        >
                          {celdaTexto(columna, fila)}
                        </Text>
                      ))}
                    </View>
                  ))}
                  <View style={estilos.filaTotal} wrap={false}>
                    <Text style={[estilos.celdaTotal, { flex: 4.7 }]}>
                      {"Total (" +
                        filas.length +
                        " renglón" +
                        (filas.length === 1 ? "" : "es") +
                        ")"}
                    </Text>
                    <Text
                      style={[estilos.celdaTotal, estilos.celdaDer, { flex: 0.9 }]}
                    >
                      {formatearCantidad(totales.cantidad)}
                    </Text>
                    <Text style={[estilos.celdaTotal, { flex: 1.3 }]}></Text>
                    <Text style={[estilos.celdaTotal, { flex: 0.8 }]}></Text>
                    <Text
                      style={[estilos.celdaTotal, estilos.celdaDer, { flex: 1.4 }]}
                    >
                      {formatearMonto(totales.importe)}
                    </Text>
                  </View>
                </View>
              )}
            </View>
          );
        })}

        <View style={estilos.firmas} wrap={false}>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>ELABORADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>REVISADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>AUTORIZADO POR</Text>
          </View>
        </View>

        <View style={estilos.pieFijo} fixed>
          <Text style={estilos.pieTexto}>
            {"ATHOS ERP | Datos Eliminados (respaldo de nota de entrega)"}
          </Text>
          <Text
            style={estilos.pieTexto}
            render={({ pageNumber, totalPages }) =>
              "Pagina " + pageNumber + " de " + totalPages
            }
          />
        </View>
      </Page>
    </Document>
  );
};



// WEBPACK FOOTER //
// ./src/components/configuracionGeneral/reporteNotaEntregaRespaldoPDF.js